import { useAuth } from "react-oidc-context";
import { useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { Icon } from "@/src/components/atoms/Icon/Icon";
import type {
    MenuItem,
    AccountMenuItem,
} from "@/src/components/templates/Dashboard/Dashboard.types";
import { usePermissions } from "@/src/lib/usePermissions/usePermissions";

export function useDashboardConfig(type: "main" | "admin") {
    const { t } = useTranslation("common", { keyPrefix: "dashboard" });
    const navigate = useNavigate();
    const auth = useAuth();
    const { hasPermission, hasAnyPermission } = usePermissions();

    const handleLogout = () => {
        void auth.signoutRedirect({
            id_token_hint: auth.user?.id_token,
            post_logout_redirect_uri: window.location.origin,
        });
    };

    const navigateToAdminPage = () => {
        navigate({ to: "/admin" });
    };

    const mainMenuItems: MenuItem[] =
        type === "main"
            ? [
                  ...(hasPermission("VERIFIABLE_QUERY_CREDENTIAL_READ")
                      ? [
                            {
                                href: "/validated-questions",
                                text: t("menu.validated-questions"),
                                icon: <Icon name="vraag-en-antwoord" />,
                            },
                        ]
                      : []),
                  ...(hasPermission(
                      "HEALTHCARE_PROVIDER_EXCERPT_CREDENTIAL_READ"
                  )
                      ? [
                            {
                                href: "/healthcare-provider-excerpts",
                                text: t(
                                    "menu.healthcare-provider-data-summaries"
                                ),
                                icon: <Icon name="delen" />,
                            },
                        ]
                      : []),
              ]
            : [
                  ...(hasPermission("SUBJECT_DIDS_READ")
                      ? [
                            {
                                href: "/admin/subject-dids",
                                text: t("menu.subject-dids"),
                                icon: <Icon name="verbinding" />,
                            },
                        ]
                      : []),
                  ...(hasPermission("USERS_READ")
                      ? [
                            {
                                href: "/admin/users",
                                text: t("menu.users"),
                                icon: <Icon name="user" />,
                            },
                        ]
                      : []),
                  ...(hasPermission("ROLES_READ")
                      ? [
                            {
                                href: "/admin/roles",
                                text: t("menu.roles"),
                                icon: <Icon name="activiteit" />,
                            },
                        ]
                      : []),
              ];

    const accountMenuItems: AccountMenuItem[] = [
        ...(hasAnyPermission(["SUBJECT_DIDS_READ", "USERS_READ", "ROLES_READ"])
            ? [
                  {
                      text: t("management"),
                      onClick: navigateToAdminPage,
                      icon: <Icon name="settings" />,
                      testId: "management",
                  },
              ]
            : []),

        {
            text: t("signout"),
            onClick: handleLogout,
            icon: <Icon name="uit-aanknop" />,
            testId: "logout",
        },
    ];

    return { mainMenuItems, accountMenuItems };
}
